import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Slider,
  Tooltip,
  Chip,
  Paper,
  LinearProgress,
  Fade,
  Zoom,
  useTheme,
  alpha,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
  SwapHoriz as SwapHorizIcon,
  VolumeUp,
  Pause,
  PlayArrow,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Circle,
  Translate as TranslateIcon,
  Language as LanguageIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Speed as SpeedIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { ContentState, Editor, EditorState, RichUtils } from "draft-js";
import {
  checkTranslator,
  checkTranslatorLang,
} from "../../services/translatorServices";
import { toast, ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";
import { addUserLogs } from "../../services/userLogServices";
import { useCheckFeatureAccess } from "../CheckFeature";
import SubscriptionRequired from "../SubscriptionRequired";
import { useCheckSubscriptionPlanAccess } from "../SubscriptionAccess";
import { checkAIRequestLimit } from "../aiRequestLimit";

const SelectDropdownMui = ({
  optionsList,
  selectLabel,
  onChange,
  value,
  error,
  ...props
}) => (
  <select
    onChange={onChange}
    value={value}
    style={{
      width: "100%",
      padding: "12px",
      borderRadius: "12px",
      border: error ? "2px solid #f44336" : "2px solid #e3f2fd",
      fontSize: "14px",
      backgroundColor: "white",
      cursor: "pointer",
      transition: "border 0.3s ease",
      boxShadow: error ? "0 0 0 2px rgba(244, 67, 54, 0.2)" : "none",
    }}
    {...props}
  >
    <option value="">{selectLabel}</option>
    {optionsList.map((item, idx) => (
      <option key={idx} value={item.value}>
        {item.name}
      </option>
    ))}
  </select>
);

const SelectDropdownSearch = ({
  optionsList,
  selectLabel,
  onChange,
  value,
  isDarkMode,
  error,
  inputRef, // Added inputRef prop for focusing
  ...props
}) => {
  const borderColor = error ? "#f44336" : "#e3f2fd";
  const backgroundColor = isDarkMode ? "#2A2A2A" : "white";
  
  return (
    <select
      ref={inputRef} // Attach the ref
      onChange={(e) => onChange(e, e.target.value)}
      value={value}
      style={{
        width: "100%",
        padding: "12px",
        borderRadius: "12px",
        border: `2px solid ${borderColor}`,
        fontSize: "14px",
        backgroundColor: backgroundColor,
        color: isDarkMode ? "white" : "black",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: error ? "0 0 0 2px rgba(244, 67, 54, 0.2)" : "none",
        outline: "none",
      }}
      {...props}
    >
      <option value="">{selectLabel}</option>
      {optionsList.map((lang, idx) => (
        <option key={idx} value={lang}>
          {lang}
        </option>
      ))}
    </select>
  );
};

const Loader = ({ loading }) => (loading ? <LinearProgress /> : null);

const ModernTranslator = () => {
  const theme = useTheme();
  const userDetails = useSelector((user) => user);
  const userID = userDetails.user.currentUser._id;
  const [inputText, setInputText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [detectedLanguage, setDetectedLanguage] = useState("");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [secondaryeditorState, setSecondaryEditorState] = useState(
    EditorState.createEmpty()
  );
  const [wordsCount, setWordsCount] = useState(0);
  const [opWordsCount, setOpWordsCount] = useState(0);
  const [wordCheck, setWordCheck] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasText, setHasText] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedProfession, setSelectedProfession] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPauseResumeToggle, setShowPauseResumeToggle] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentSpeed, setCurrentSpeed] = useState(1);
  const [isSliderMoving, setIsSliderMoving] = useState(false);
  const [speakLanguage, setSpeakLanguage] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [languageError, setLanguageError] = useState(false);
  const [hasTranslatedOnce, setHasTranslatedOnce] = useState(false);
  const [shouldFocusLanguage, setShouldFocusLanguage] = useState(false); // New state to trigger focus
  const accessToTranslate = useCheckFeatureAccess("translator", "translator");
  const accessToTranslatorScreen = useCheckSubscriptionPlanAccess("Translator");
  const accessToSpeaker = useCheckSubscriptionPlanAccess("speaker")
  const [subReqOpen, setSubReqOpen] = useState(false);
  const [isFeatureInside, setIsFeatureInside] = useState(false);

  // Refs
  const prevSelectedLanguageRef = useRef("");
  const prevEditorContentRef = useRef("");
  const languageSelectRef = useRef(null); // Ref for the language dropdown

  const languageList = [
    "Arabic",
    "Assamese",
    "Bengali",
    "Cebuano",
    "Chinese",
    "Czech",
    "Danish",
    "Dutch",
    "English",
    "Finnish",
    "French",
    "German",
    "Greek",
    "Gujarati",
    "Hebrew",
    "Hindi",
    "Hungarian",
    "Indonesian",
    "Italian",
    "Japanese",
    "Kannada",
    "Korean",
    "Malay",
    "Malayalam",
    "Marathi",
    "Nepali",
    "Norwegian",
    "Odia",
    "Persian",
    "Polish",
    "Portuguese",
    "Punjabi",
    "Romanian",
    "Russian",
    "Sanskrit",
    "Spanish",
    "Swedish",
    "Tagalog",
    "Tamil",
    "Telugu",
    "Thai",
    "Turkish",
    "Urdu",
    "Ukrainian",
    "Vietnamese",
  ];

  const industryList = [
    { value: "IT", name: "IT" },
    { value: "Healthcare", name: "Healthcare" },
    { value: "Finance", name: "Finance" },
    { value: "Agriculture", name: "Agriculture" },
    { value: "Education", name: "Education" },
    { value: "Retail", name: "Retail" },
    { value: "Telecommunication", name: "Telecommunication" },
  ];

  const professionList = [
    { value: "Content Writer", name: "Content Writer" },
    { value: "Business Owner", name: "Business Owner" },
    { value: "HR Professional", name: "HR Professional" },
    { value: "Marketing", name: "Marketing" },
    { value: "Product Manager", name: "Product Manager" },
    { value: "Medical Professional", name: "Medical Professional" },
    { value: "Legal Professional", name: "Legal Professional" },
  ];

  useEffect(() => {
    if (accessToTranslatorScreen == false) {
      setSubReqOpen(true);
    }
  }, [accessToTranslatorScreen]);

  useEffect(() => {
    const updateTheme = () => {
      const theme = localStorage.getItem("ThemeSwitch");
      setIsDarkMode(theme === "true");
    };
    updateTheme();
    window.addEventListener("storage", updateTheme);
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, arguments);
      if (key === "ThemeSwitch") {
        updateTheme();
      }
    };
    return () => {
      window.removeEventListener("storage", updateTheme);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (isPlaying) {
        window.speechSynthesis.cancel();
        setIsPlaying(false);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    console.log("Detector_", inputText, wordCheck);
    if (inputText !== "" && wordCheck !== "" && accessToTranslate) {
      handleDetect();
    }
  }, [wordCheck]);

  // Effect to focus language dropdown when error occurs
  useEffect(() => {
    if (shouldFocusLanguage && languageSelectRef.current) {
      languageSelectRef.current.focus();
      setShouldFocusLanguage(false);
      
      // Scroll to the language dropdown section
      const languageSection = document.getElementById('translate-to-section');
      if (languageSection) {
        languageSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
  }, [shouldFocusLanguage]);

  // Effect to auto-translate when language changes after first translation
  useEffect(() => {
    if (
      hasTranslatedOnce &&
      selectedLanguage &&
      selectedLanguage !== prevSelectedLanguageRef.current &&
      inputText &&
      !loading
    ) {
      console.log("Auto-translating due to language change...");
      handleTranslate(false);
    }
    
    prevSelectedLanguageRef.current = selectedLanguage;
  }, [selectedLanguage, inputText]);

  // Effect to auto-translate when source text changes after first translation
  useEffect(() => {
    const currentContent = editorState.getCurrentContent().getPlainText();
    
    if (
      hasTranslatedOnce &&
      selectedLanguage &&
      currentContent &&
      currentContent !== prevEditorContentRef.current &&
      !loading
    ) {
      console.log("Auto-translating due to source text change...");
      handleTranslate(false);
    }
    
    prevEditorContentRef.current = currentContent;
  }, [editorState]);

  const handleListen = () => {
    if(accessToSpeaker == false){
      setSubReqOpen(true)
      setIsFeatureInside(true)
      return
    }

    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const textToSpeak = secondaryeditorState
        .getCurrentContent()
        .getPlainText();
      const utterance = new SpeechSynthesisUtterance(textToSpeak);

      const voices = window.speechSynthesis.getVoices();
      console.log("Available Voices:", voices);

      const femaleVoices = voices.filter(
        (voice) =>
          voice.name.includes("Microsoft Zira - English (United States)") &&
          voice.lang.includes(speakLanguage.toLowerCase())
      );

      console.log("femaleVoices:", femaleVoices);

      if (femaleVoices.length > 0) {
        utterance.voice = femaleVoices[0];
      } else {
        console.warn("Female voice not found");
      }

      utterance.lang = speakLanguage;
      utterance.rate = currentSpeed;

      utterance.onend = () => {
        console.log("Speech synthesis completed");
        setIsPlaying(false);
        setShowPauseResumeToggle(false);
      };

      if (!isPlaying) {
        window.speechSynthesis.speak(utterance);
        setIsPlaying(true);
        setShowPauseResumeToggle(true);
      } else {
        window.speechSynthesis.pause();
        setIsPlaying(false);
        setShowPauseResumeToggle(false);
      }
    } else {
      console.error("Speech synthesis not supported in this browser.");
    }
  };

  const handleSpeedChange = (_, newValue) => {
    if (!isSliderMoving) {
      setSpeed(newValue);
      setCurrentSpeed(newValue);

      if (isPlaying) {
        const textToSpeak = secondaryeditorState
          .getCurrentContent()
          .getPlainText();
        const utterance = new SpeechSynthesisUtterance(textToSpeak);

        const voices = window.speechSynthesis.getVoices();

        const femaleVoices = voices.filter(
          (voice) =>
            voice.name.includes("Microsoft Zira - English (United States)") &&
            voice.lang.includes(speakLanguage.toLowerCase())
        );

        if (femaleVoices.length > 0) {
          utterance.voice = femaleVoices[0];
        } else {
          console.warn("Female voice not found. Using default voice.");
        }

        utterance.lang = speakLanguage;
        utterance.rate = newValue;

        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      window.speechSynthesis.pause();
    } else {
      window.speechSynthesis.resume();
    }
    setIsPlaying(!isPlaying);
  };

  const clearListenFunctionality = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setShowPauseResumeToggle(false);
  };

  const handleOuputChange = (newEditorState) => {
    const contentState = newEditorState.getCurrentContent();
    setSecondaryEditorState(newEditorState);

    if (contentState.hasText() === false) {
      clearListenFunctionality();
    }
  };

  const handleDetect = async () => {
    try {
      if (inputText === "") {
        return;
      }
      let text = inputText.split(" ")[0];
      console.log("texttttttttttttt", inputText);
      const response = await checkTranslatorLang(text);
      const detectedLanguage = response.data[0];
      console.log("detectedLanguage:", detectedLanguage);
      setDetectedLanguage(detectedLanguage);
    } catch (error) {
      console.error("Error calling backend API:", error.message);
    }
  };

  const handleTranslate = async (isManual = true) => {
    // Reset error state
    setLanguageError(false);

    // Validate language selection FIRST
    if (!selectedLanguage) {
      if (isManual) {
        setLanguageError(true);
        toast.warn("Please select a language to translate to");
        // Set flag to focus on language dropdown
        setShouldFocusLanguage(true);
      }
      setLoading(false);
      return;
    }

    const checkailimit = await checkAIRequestLimit(userID);
    console.log(checkailimit, "_checkailimit");
    if (checkailimit == false) {
      toast.warn("AI request limit reached.");
      return;
    }

    if (
      editorState.getCurrentContent().getPlainText().trim().split(/\s+/)
        .length > 1500
    ) {
      toast.warn("Words limit exceeded");
      return;
    }

    setLoading(true);
    try {
      let input;
      const selection = editorState.getSelection();
      input = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getAnchorKey())
        .getText()
        .slice(selection.getStartOffset(), selection.getEndOffset());
      if (!input) {
        input = editorState.getCurrentContent().getPlainText();
      }

      if (input === "") {
        if (isManual) {
          toast.warn("Please enter text to translate");
        }
        setLoading(false);
        return;
      }

      console.log(
        "SelectedLang_",
        selectedLanguage,
        selectedIndustry,
        selectedProfession
      );

      let data;
      if (selectedProfession !== "" && selectedIndustry !== "") {
        data = {
          selectedLanguage,
          selectedIndustry,
          selectedProfession,
          input,
          userId: userID,
        };
      } else if (selectedProfession !== "") {
        data = {
          selectedLanguage,
          selectedProfession,
          input,
          userId: userID,
        };
      } else if (selectedIndustry !== "") {
        data = {
          selectedLanguage,
          selectedIndustry,
          input,
          userId: userID,
        };
      } else {
        data = {
          selectedLanguage,
          input,
          userId: userID,
          isCoAuthor: false,
        };
      }

      const response = await checkTranslator(data);
      const translatedText = response?.data?.output;
      console.log("translatedText", translatedText);

      if (response.status == 400) {
        toast.warn(response.message);
        return;
      }

      const data1 = {
        userId: sessionStorage.getItem("userId"),
        organizationId: sessionStorage.getItem("organizationId"),
        actionType: "translator",
      };

      const addLogs = await addUserLogs(data1);
      if (addLogs.status !== 200) {
        toast.error(addLogs.message);
      }

      let textWithoutLeadingNewlines = translatedText[0].replace(/^\n+/, "");
      let wordLength = translatedText[0].split(/[ -]+/);

      setOpWordsCount(wordLength.length);
      const contentState = ContentState.createFromText(
        textWithoutLeadingNewlines
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setTranslatedText(translatedText);
      setSecondaryEditorState(newEditorState);
      
      if (!hasTranslatedOnce) {
        setHasTranslatedOnce(true);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error calling backend API:", error.message);
      toast.warn(error.message);
      setLoading(false);
    }
  };

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);

    const plainTextContent = newEditorState.getCurrentContent().getPlainText();
    setInputText(plainTextContent);

    if (plainTextContent === "") {
      setDetectedLanguage("");
      setSelectedIndustry("");
      setSelectedProfession("");
      setWordsCount(0);
      setOpWordsCount(0);
      setHasText(false);
    } else {
      setHasText(true);
    }

    if (plainTextContent !== "") {
      const trimmedText = plainTextContent.trim();
      const words = trimmedText
        .split(/\s+/)
        .filter((word) => word !== "").length;
      let splitinput = plainTextContent.split(" ");
      if (splitinput.length > 1) {
        setWordCheck(splitinput[0]);
      }
      console.log("words_", words);
      setWordsCount(words);
    }
  };

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    console.log("editorstate_", newState);
    if (newState) {
      handleEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const handlecopy = () => {
    let text = secondaryeditorState.getCurrentContent().getPlainText();
    if (text == "") {
      toast.warn(`You don't have text, to copy`);
      return;
    }
    navigator.clipboard.writeText(text).then(
      function () {
        console.log("Async: Copying to clipboard was successful!");
        if (text) toast.info("Your text Copied!");
      },
      function (err) {
        console.error("Async: Could not copy text: ", err);
      }
    );
  };

  const handleDownload = () => {
    if (secondaryeditorState.getCurrentContent().getPlainText() == "") {
      toast.warn(`You don't have text, to download`);
      return;
    }
    const blob = new Blob(
      [secondaryeditorState.getCurrentContent().getPlainText()],
      { type: "text/plain" }
    );

    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = "text_content.doc";
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  const handleLanguage = (event, value) => {
    console.log("e_", event);
    console.log("V_", value);

    // Reset error when user selects a language
    if (value) {
      setLanguageError(false);
    }

    if (value !== selectedLanguage) {
      setSecondaryEditorState(EditorState.createEmpty());
      clearListenFunctionality();
    }
    setSelectedLanguage(value);
  };

  const handleDelete = () => {
    setEditorState(EditorState.createEmpty());
    setSecondaryEditorState(EditorState.createEmpty());
    setInputText("");
    setTranslatedText("");
    setDetectedLanguage("");
    setSelectedIndustry("");
    setSelectedProfession("");
    setSelectedLanguage("");
    setWordsCount(0);
    setOpWordsCount(0);
    setWordCheck("");
    setHasText(false);
    setLanguageError(false);
    setHasTranslatedOnce(false);
    clearListenFunctionality();
  };

  const handleInterchange = () => {
    if (!secondaryeditorState.getCurrentContent().hasText()) {
      return;
    }
    const temp = detectedLanguage;
    console.log("temp_", temp);
    setDetectedLanguage(selectedLanguage);
    setSelectedLanguage(temp);

    const tempText = inputText;
    setInputText(translatedText);
    setTranslatedText(tempText);

    const tempEditorState = editorState;
    console.log("editor_", tempEditorState, secondaryeditorState);
    setEditorState(secondaryeditorState);
    setSecondaryEditorState(tempEditorState);

    const firstEditor = secondaryeditorState.getCurrentContent().getPlainText();
    const secondEditor = tempEditorState.getCurrentContent().getPlainText();
    handleWordsCount(firstEditor, "first");
    handleWordsCount(secondEditor, "second");
  };

  const handleWordsCount = (text, type) => {
    const trimmedText = text.trim();
    const words = trimmedText.split(/\s+/).filter((word) => word !== "").length;
    console.log("words_", words);
    if (type == "first") {
      setWordsCount(words);
    } else {
      setOpWordsCount(words);
    }
  };

  return (
    <>
    <SubscriptionRequired open={subReqOpen} setOpen={setSubReqOpen} isFeatureInside={isFeatureInside} setIsFeatureInside={setIsFeatureInside} path={"translator"}/>
    
    <Box
      sx={{
        minHeight: "100vh",
        background: isDarkMode
          ? "linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 100%)"
          : "linear-gradient(135deg, #ffffffff 0%, #d3d1d1ff)",
        p: 1.5,
        transition: "all 0.3s ease",
      }}
    >
      <ToastContainer />
      <Fade in timeout={800}>
        <Paper
          elevation={6}
          sx={{
            p: 2,
            mb: 1.5,
            borderRadius: "24px",
            background: isDarkMode
              ? "linear-gradient(145deg, #2a2a2a, #1f1f1f)"
              : "linear-gradient(145deg, #ffffff, #f8f9fa)",
            backdropFilter: "blur(10px)",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <TranslateIcon
                sx={{
                  fontSize: 40,
                  color: "primary.main",
                  mr: 2,
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "primary.main",
                  lineHeight: 1.3,
                }}
              >
                AI Translator
              </Typography>
            </Box>
            <Box>
              <Link to="/docTags">
                 <Button
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "#fff",
                      borderRadius: 3,
                      whiteSpace: "nowrap",
                      minWidth: "120px",
                      px: 3,
                      py: 1.2,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.95rem",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                      },
                    }}
                  >
                    ← Back
                  </Button>

              </Link>
            </Box>
          </Box>
        </Paper>
      </Fade>

      <Zoom in timeout={1000}>
        <Paper
          elevation={4}
          sx={{
            p: 3,
            mb: 1.5,
            borderRadius: "20px",
            background: isDarkMode
              ? alpha("#2a2a2a", 0.9)
              : alpha("#ffffff", 0.95),
            backdropFilter: "blur(15px)",
          }}
        >
          <Grid2 container spacing={3} alignItems="center">
            <Grid2 xs={12} sm={6} md={2}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <LanguageIcon
                    sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    Detected Language
                  </Typography>
                </Box>
                <Chip
                  icon={<LanguageIcon sx={{ fontSize: 16 }} />}
                  label={detectedLanguage || "Auto-detect"}
                  variant={detectedLanguage ? "filled" : "outlined"}
                  color={detectedLanguage ? "primary" : "default"}
                  sx={{
                    mt: 0.7,
                    minWidth: "150px",
                    fontWeight: 600,
                    height: "40px",
                    fontSize: "0.9rem",
                    borderRadius: "10px",
                    backgroundColor: detectedLanguage
                      ? "primary.main"
                      : isDarkMode
                      ? "black.800"
                      : "black.100",
                    color: detectedLanguage ? "white" : "text.secondary",
                  }}
                />
              </Box>
            </Grid2>

            <Grid2 xs={12} sm={6} md={2}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <WorkIcon
                    sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    Profession
                  </Typography>
                </Box>
                <SelectDropdownMui
                  optionsList={professionList}
                  selectLabel="Select..."
                  onChange={(e) => setSelectedProfession(e.target.value)}
                  value={selectedProfession}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      height: "40px",
                      minWidth: "160px",
                      "& .MuiSelect-select": {
                        color: selectedProfession
                          ? "text.primary"
                          : "text.disabled",
                        fontWeight: selectedProfession ? 500 : 400,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "text.disabled",
                    },
                  }}
                />
              </Box>
            </Grid2>
            <Grid2 xs={12} sm={6} md={2}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <BusinessIcon
                    sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    Industry
                  </Typography>
                </Box>
                <SelectDropdownMui
                  optionsList={industryList}
                  selectLabel="Select..."
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  value={selectedIndustry}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      height: "40px",
                      minWidth: "160px",
                      "& .MuiSelect-select": {
                        padding: "8px 14px",
                        paddingRight: "32px !important",
                        color: selectedIndustry
                          ? "text.primary"
                          : "text.disabled",
                        fontWeight: selectedIndustry ? 500 : 400,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "text.disabled",
                    },
                  }}
                />
              </Box>
            </Grid2>
            <Grid2 xs={12} sm={6} md={2}>
              <Box id="translate-to-section"> {/* Added ID for scrolling */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <TranslateIcon
                    sx={{ 
                      fontSize: 18, 
                      color: languageError ? "#f44336" : "text.secondary", 
                      mr: 1 
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: languageError ? "#f44336" : "text.secondary",
                      fontWeight: 600,
                    }}
                  >
                    Translate To
                    <span style={{ 
                      color: "#f44336", 
                      marginLeft: "4px",
                      fontWeight: "bold" 
                    }}>
                      *
                    </span>
                  </Typography>
                </Box>
                <SelectDropdownSearch
                  optionsList={languageList}
                  selectLabel="Select..."
                  onChange={handleLanguage}
                  value={selectedLanguage}
                  isDarkMode={isDarkMode}
                  error={languageError}
                  inputRef={languageSelectRef} // Pass the ref to the component
                />
                {languageError && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#f44336",
                      fontSize: "0.75rem",
                      mt: 0.5,
                      display: "block",
                      fontWeight: 500,
                    }}
                  >
                    Please select a language to translate to
                  </Typography>
                )}
              </Box>
            </Grid2>
            <Grid2 xs={12} sm={6} md={1.5}>
              <Box sx={{ textAlign: "center", mt: { xs: 2, sm: 3 } }}>
                <Tooltip title="Clear All Content">
                  <Button
                    onClick={handleDelete}
                    variant="outlined"
                    color="error"
                    startIcon={<ClearIcon />}
                    disabled={!hasText}
                    sx={{
                      mt: 0.7,
                      borderRadius: "10px",
                      height: "40px",
                      minWidth: "120px",
                      textTransform: "none",
                      fontWeight: 600,
                      borderWidth: "2px",
                      "&:hover": {
                        borderWidth: "2px",
                        backgroundColor: alpha(
                          theme.palette.error.main,
                          0.04
                        ),
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 8px rgba(244, 67, 54, 0.2)",
                      },
                      transition: "all 0.2s ease",
                      "&:disabled": {
                        borderColor: "black.300",
                        color: "black.500",
                      },
                    }}
                  >
                    Clear All
                  </Button>
                </Tooltip>
              </Box>
            </Grid2>
          </Grid2>
        </Paper>
      </Zoom>

      <Loader loading={loading} />
      <Grid2 container spacing={3}>
        <Grid2 xs={12} md={6}>
          <Fade in timeout={1200}>
            <Card
              elevation={8}
              sx={{
                height: "100%",
                borderRadius: "20px",
                overflow: "hidden",
                background: isDarkMode
                  ? "linear-gradient(145deg, #2a2a2a, #1f1f1f)"
                  : "linear-gradient(145deg, #ffffff, #f8f9fa)",
                backdropFilter: "blur(10px)",
                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <CardContent
                sx={{
                  p: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    background:
                      "linear-gradient(90deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))",
                    borderBottom: `1px solid ${alpha(
                      theme.palette.divider,
                      0.1
                    )}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Circle sx={{ fontSize: 12, mr: 1 }} />
                        Source Text
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Tooltip title="Swap Languages" placement="top">
                        <IconButton
                          onClick={handleInterchange}
                          disabled={
                            !secondaryeditorState
                              .getCurrentContent()
                              .hasText()
                          }
                          sx={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "white",
                            width: 38,
                            height: 37,
                            borderRadius: "20px",
                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                              transform: "translateY(-2px)",
                              boxShadow:
                                "0 6px 16px rgba(102, 126, 234, 0.4)",
                            },
                            "&:active": {
                              transform: "translateY(0)",
                            },
                            "&:disabled": {
                              background: "black.300",
                              color: "black.500",
                              boxShadow: "none",
                              transform: "none",
                            },
                            transition:
                              "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          <SwapHorizIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: 3,
                    minHeight: "400px",
                    "& .DraftEditor-root": {
                      minHeight: "400px",
                      maxHeight: "400px",
                      overflow: "scroll",
                      fontSize: "16px",
                      lineHeight: 1.6,
                      color: isDarkMode ? "white" : "text.primary",
                    },
                    "& .public-DraftEditorPlaceholder-root": {
                      color: "text.secondary",
                      fontSize: "16px",
                    },
                  }}
                >
                  <Editor
                    editorState={editorState}
                    onChange={handleEditorChange}
                    placeholder="Enter your text to translate..."
                  />
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderTop: `1px solid ${alpha(
                      theme.palette.divider,
                      0.1
                    )}`,
                    background: alpha(theme.palette.background.paper, 0.5),
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Chip
                    icon={<Circle sx={{ fontSize: "12px !important" }} />}
                    label={`${wordsCount}/1500 Words`}
                    variant="outlined"
                    size="small"
                    color={wordsCount > 1400 ? "warning" : "primary"}
                  />

                  <Button
                    variant="contained"
                    onClick={() => handleTranslate(true)}
                    disabled={
                      !hasText ||
                      !selectedLanguage ||
                      !accessToTranslate ||
                      loading
                    }
                    startIcon={<TranslateIcon />}
                    sx={{
                      borderRadius: "12px",
                      px: 3,
                      py: 1,
                      background: "linear-gradient(45deg, #75dab1ff, #38885faf)",
                      "&:hover": {
                        background:
                          "linear-gradient(45deg, #5a6fd8, #6a4190)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(102,126,234,0.4)",
                      },
                      "&:disabled": {
                        background: "black.300",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {loading ? "Translating..." : "Translate"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid2>
        <Grid2 xs={12} md={6}>
          <Fade in timeout={1400}>
            <Card
              elevation={8}
              sx={{
                height: "100%",
                borderRadius: "20px",
                overflow: "hidden",
                background: isDarkMode
                  ? "linear-gradient(145deg, #2a2a2a, #1f1f1f)"
                  : "linear-gradient(145deg, #ffffff, #f8f9fa)",
                backdropFilter: "blur(10px)",
                border: `2px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
              }}
            >
              <CardContent
                sx={{
                  p: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    background:
                      "linear-gradient(90deg, rgba(118,75,162,0.1), rgba(102,126,234,0.1))",
                    borderBottom: `1px solid ${alpha(
                      theme.palette.divider,
                      0.1
                    )}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "secondary.main",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Circle sx={{ fontSize: 12, mr: 1 }} />
                    Translation
                    {selectedLanguage && (
                      <Chip
                        label={selectedLanguage}
                        size="small"
                        sx={{ ml: 2, fontWeight: 600 }}
                      />
                    )}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: 3,
                    minHeight: "400px",
                    "& .DraftEditor-root": {
                      minHeight: "400px",
                      maxHeight: "400px",
                      overflow: "scroll",
                      fontSize: "16px",
                      lineHeight: 1.6,
                      color: isDarkMode ? "white" : "text.primary",
                    },
                  }}
                >
                  <Editor
                    editorState={secondaryeditorState}
                    onChange={handleOuputChange}
                    readOnly={true}
                    placeholder="Translation will appear here..."
                  />
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderTop: `1px solid ${alpha(
                      theme.palette.divider,
                      0.1
                    )}`,
                    background: alpha(theme.palette.background.paper, 0.5),
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Chip
                    icon={<Circle sx={{ fontSize: "12px !important" }} />}
                    label={`${opWordsCount}/1500 Words`}
                    variant="outlined"
                    size="small"
                    color="secondary"
                  />

                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Tooltip title="Download">
                      <IconButton
                        onClick={handleDownload}
                        disabled={!opWordsCount}
                        sx={{
                          color: "success.main",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.success.main,
                              0.1
                            ),
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Copy">
                      <IconButton
                        onClick={handlecopy}
                        disabled={!opWordsCount}
                        sx={{
                          color: "info.main",
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Listen">
                      <IconButton
                        onClick={handleListen}
                        disabled={!opWordsCount}
                        sx={{
                          color: "warning.main",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.warning.main,
                              0.1
                            ),
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <VolumeUp />
                      </IconButton>
                    </Tooltip>

                    {showPauseResumeToggle && (
                      <>
                        <Tooltip title={isPlaying ? "Pause" : "Resume"}>
                          <IconButton
                            onClick={handleTogglePlay}
                            sx={{ color: "text.secondary" }}
                          >
                            {isPlaying ? <Pause /> : <PlayArrow />}
                          </IconButton>
                        </Tooltip>

                        <Box sx={{ width: 100, ml: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <SpeedIcon
                              sx={{ fontSize: 16, color: "text.secondary" }}
                            />
                            <Slider
                              value={speed}
                              min={0.25}
                              max={2}
                              step={0.25}
                              onChange={handleSpeedChange}
                              valueLabelDisplay="auto"
                              valueLabelFormat={(value) => `${value}x`}
                              size="small"
                              sx={{
                                "& .MuiSlider-thumb": {
                                  width: 16,
                                  height: 16,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid2>
      </Grid2>
    </Box>
    </>
  );
};

export default ModernTranslator;
